import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Check if auth service is ready before proceeding
  if (!authService.isServiceReady()) {
    console.warn('Auth service not ready, skipping interceptor');
    return next(req);
  }

  const token = authService.getToken();

  // Debug logging for problematic requests
  if (req.url.includes('localhost') || req.url.includes('127.0.0.1')) {
    console.log('Auth interceptor processing:', req.method, req.url);
  }

  // Skip interceptor for certain requests to prevent infinite loops
  if (req.url.includes('/auth/login') || req.url.includes('/auth/logout')) {
    console.log('Skipping auth interceptor for auth request:', req.url);
    return next(req);
  }

  // Skip for WebSocket upgrade requests (101 status)
  if (req.headers.has('Upgrade') && req.headers.get('Upgrade')?.toLowerCase() === 'websocket') {
    console.log('Skipping auth interceptor for WebSocket request');
    return next(req);
  }

  // Skip for SSE (Server-Sent Events) requests
  if (req.headers.has('Accept') && req.headers.get('Accept')?.includes('text/event-stream')) {
    console.log('Skipping auth interceptor for SSE request');
    return next(req);
  }

  // Skip for certain file types that don't need authentication
  if (req.url.includes('.json') || req.url.includes('.js') || req.url.includes('.css') || req.url.includes('.png') || req.url.includes('.jpg')) {
    console.log('Skipping auth interceptor for static file:', req.url);
    return next(req);
  }

  if (token) {
    let headers = req.headers.set('Authorization', `Bearer ${token}`);
    headers = headers.delete('X-Skip-Logout');
    const authReq = req.clone({ headers });
    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log('Auth interceptor error:', error.status, error.url);

        // 401 = invalid/expired session; 403 = authorization (handled by callers)
        if (error.status === 401) {
          const skipLogout = req.headers.has('X-Skip-Logout');
          if (!skipLogout && !req.url.includes('/auth/login') && authService.isServiceReady()) {
            try {
              // Add a small delay to prevent rapid successive calls
              setTimeout(() => {
                if (authService.isServiceReady()) {
                  authService.handleSessionExpiration();
                }
              }, 100);
            } catch (err) {
              console.error('Error in auth interceptor:', err);
            }
          }
        }
        // Don't throw error for 101 status (Switching Protocols)
        if (error.status === 101) {
          console.warn('WebSocket upgrade request detected, skipping error handling');
          return throwError(() => error);
        }
        return throwError(() => error);
      })
    );
  }

  return next(req);
};
